import type { ReactNode } from 'react';
import { AmbientMesh } from './AmbientMesh';
import { Surface } from './ui';
import { Wordmark } from './Mark';

/**
 * AuthLayout — split brand stripe + glass form layout shared by
 * Login, Signup, and AcceptInvite.
 *
 * Mobile: form fills the viewport, brand stripe collapses to a top
 * header strip with just the logo + tagline.
 *
 * Desktop: two columns — left 55% is the AmbientMesh + brand pitch,
 * right 45% holds the form card. The form card is `Surface tone="raised"`
 * so it picks up the brand's glass treatment automatically.
 */
export function AuthLayout({
  title,
  subtitle,
  topRight,
  children,
  tagline = "The bio link that actually opens in Safari.",
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Slot for a "Need an account?" / "Sign in instead" link in the top-right. */
  topRight?: ReactNode;
  children: ReactNode;
  tagline?: string;
}) {
  return (
    <div className="relative min-h-screen">
      <AmbientMesh />

      <div className="relative z-10 mx-auto grid min-h-screen max-w-7xl grid-cols-1 md:grid-cols-[1.2fr_1fr]">
        {/* ── Brand stripe (desktop only) ─────────────────────── */}
        <aside className="hidden flex-col justify-between p-12 md:flex">
          <Wordmark to="/" size="md" />

          <div className="max-w-md">
            <h2 className="font-display text-4xl font-extrabold leading-[1.1] tracking-[-0.03em]">
              {tagline}
            </h2>
            <p className="mt-5 text-[var(--color-on-surface-variant)]">
              Real analytics. Real Speed. Real revenue. Built for creators who
              refuse to bleed conversions to Meta&apos;s in-app browser.
            </p>
          </div>

          <p className="text-xs text-[var(--color-on-surface-variant)] opacity-60">
            © 2026 Poof
          </p>
        </aside>

        {/* ── Form pane ───────────────────────────────────────── */}
        <main className="flex flex-col px-6 py-10 md:items-center md:justify-center md:p-12">
          {/* Mobile-only top header (replaces the stripe) */}
          <div className="mb-8 flex items-center justify-between md:hidden">
            <Wordmark to="/" size="sm" />
            {topRight}
          </div>

          <Surface tone="raised" className="w-full max-w-md p-8 md:p-10">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="font-display text-3xl font-bold tracking-tight md:text-4xl">
                  {title}
                </h1>
                {subtitle && (
                  <p className="mt-2 text-[var(--color-on-surface-variant)]">
                    {subtitle}
                  </p>
                )}
              </div>
              <div className="hidden shrink-0 md:block">{topRight}</div>
            </div>

            <div className="mt-7">{children}</div>
          </Surface>
        </main>
      </div>
    </div>
  );
}

/**
 * AuthDivider — the "or with email" rule used by Login + Signup between
 * Google OAuth and the email form. Pulled out so the spacing + token
 * usage stays consistent.
 */
export function AuthDivider({ label = 'or with email' }: { label?: string }) {
  return (
    <div className="my-6 flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-on-surface-variant)]">
      <span className="h-px flex-1 bg-[var(--color-border)]" />
      {label}
      <span className="h-px flex-1 bg-[var(--color-border)]" />
    </div>
  );
}

/**
 * AuthTextField — labeled text input with the brand's deep-surface inset
 * treatment. Used by Login + Signup forms. Forwards every input prop.
 */
export function AuthTextField({
  label,
  ...rest
}: { label: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-[0.06em] text-[var(--color-on-surface-variant)]">
        {label}
      </span>
      <input
        {...rest}
        className="rounded-xl border border-[var(--color-border)] bg-[var(--surface-deep)] px-4 py-3 text-[var(--color-foreground)] outline-none transition placeholder:text-[var(--color-on-surface-variant)] focus:border-[var(--color-ring)]"
      />
    </label>
  );
}

/**
 * GoogleIcon — Google's official multi-color G mark. Shared by every
 * OAuth button so we don't ship three subtly-different copies.
 */
export function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#EA4335"
        d="M12 10.2v3.9h5.5c-.2 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.7 3.4 14.6 2.5 12 2.5 6.9 2.5 2.8 6.6 2.8 11.7s4.1 9.2 9.2 9.2c5.3 0 8.8-3.7 8.8-8.9 0-.6-.1-1.1-.2-1.7H12z"
      />
    </svg>
  );
}
